import React, {useState, useEffect} from "react";
import {useGetTicketByUniqueCode} from "../../hooks/Administrative/useGetTicketByUniqueCode";
import "./SupervisorTicketInfo.css";
import {format} from "date-fns";
import {es} from "date-fns/locale/es";
import {useGetTechnician} from "../../hooks/Supervisor/useGetTechnician";
import {useAssignTicket} from "../../hooks/Supervisor/useAssignTicket";
import {useAddBudget} from "../../hooks/Supervisor/useAddBudget";
import {BudgetDto} from "../../services/Supervisor/ticketService";

const ticketStateMap: Map<string, string> = new Map([
    ["CREATED", "Creado"],
    ["APPROVED", "Aprobado"],
    ["ASSIGNED", "Asignado"],
    ["IN_PROGRESS", "En progreso"],
    ["BLOCKED", "Bloqueado"],
    ["IN_REVIEW", "En revisión"],
    ["COMPLETED", "Completado"],
]);

const serviceTypeMap: Map<string, string> = new Map([
    ["PREVENTIVE", "Preventivo"],
    ["CORRECTIVE", "Correctivo"],
    ["INSTALLATION", "Instalación"],
    ["VERIFICATION", "Verificación"],
]);

const SupervisorTicketInfo = ({additionalInfo, ticketCode, onBack, showTicketInfo, updateTicketStateLocally}: {
    additionalInfo: any,
    ticketCode: string,
    onBack: () => void,
    showTicketInfo: (show: boolean) => void,
    updateTicketStateLocally: (newState: any, ticketUniqueCode: any) => void
}) => {
    const {ticket, updateTicketTechnicianLocally, updateTicketBudgetLocally} = useGetTicketByUniqueCode(ticketCode);
    const [isModalVisible, setModalVisible] = useState(false);
    const [isBudgetModalVisible, setBudgetModalVisible] = useState(false);
    const {technicians, isLoading} = useGetTechnician();
    const [selectedTechnician, setSelectedTechnician] = useState<string>("");
    const [selectedTechnicianName, setSelectedTechnicianName] = useState<string>("");
    const [serviceDate, setServiceDate] = useState<string>("");
    const {assignTicketToTechnician, assigningTechnician} = useAssignTicket();
    const {addBudget, addingBudget} = useAddBudget();

    // Budget modal states
    const [serviceCost, setServiceCost] = useState<string>("");
    const [materialsRefacciones, setMaterialsRefacciones] = useState<{name: string, cost: string}[]>([{name: "", cost: ""}]);
    const [equipmentCosts, setEquipmentCosts] = useState<{[key: string]: string}>({});
    const [viaticos, setViaticos] = useState<string>("");
    const formattedDate = ticket?.createdAt
        ? format(new Date(ticket.createdAt.replace(" ", "T")), "dd MMMM yyyy, HH:mm:ss", {locale: es})
        : "N/A";

    useEffect(() => {
        if (ticket?.budget && ticket.budget.length > 0) {
            // Prellenar servicio
            const serviceItem = ticket.budget.find((item: BudgetDto) => item.conceptType === 'SERVICE');
            if (serviceItem) {
                setServiceCost(serviceItem.amount.toString());
            }

            // Prellenar materiales/refacciones
            const materials = ticket.budget.filter((item: BudgetDto) => item.conceptType === 'MATERIAL');
            if (materials.length > 0) {
                setMaterialsRefacciones(materials.map((item: BudgetDto) => ({
                    name: item.description,
                    cost: item.amount.toString()
                })));
            }

            // Prellenar equipos
            const equipments = ticket.budget.filter((item: BudgetDto) => item.conceptType === 'EQUIPMENT');
            if (equipments.length > 0) {
                const equipmentCostsObj: {[key: string]: string} = {};
                equipments.forEach((item: BudgetDto) => {
                    equipmentCostsObj[item.description] = item.amount.toString();
                });
                setEquipmentCosts(equipmentCostsObj);
            }

            // Prellenar viáticos
            const travelExpenses = ticket.budget.find((item: BudgetDto) => item.conceptType === 'TRAVEL_EXPENSES');
            if (travelExpenses) {
                setViaticos(travelExpenses.amount.toString());
            }
        }
    }, [ticket?.budget]);

    const handleAssignTicketIcon = () => {
        setModalVisible(true);
    };

    const handleAddBudget = () => {
        setBudgetModalVisible(true);
    };

    const closeBudgetModal = () => {
        setBudgetModalVisible(false);
    };

    const closeAssignModal = () => {
        setModalVisible(false);
    };

    const addMaterialRefaccion = () => {
        setMaterialsRefacciones([...materialsRefacciones, {name: "", cost: ""}]);
    };

    const removeMaterialRefaccion = (index: number) => {
        setMaterialsRefacciones(materialsRefacciones.filter((_, i) => i !== index));
    };

    const updateMaterialRefaccion = (index: number, field: "name" | "cost", value: string) => {
        const updated = [...materialsRefacciones];
        updated[index][field] = value;
        setMaterialsRefacciones(updated);
    };

    const updateEquipmentCost = (equipment: string, cost: string) => {
        setEquipmentCosts({...equipmentCosts, [equipment]: cost});
    };

    const calculateTotalCost = () => {
        const service = parseFloat(serviceCost) || 0;
        const materialsTotal = materialsRefacciones.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
        const equipmentsTotal = Object.values(equipmentCosts).reduce((sum, cost) => sum + (parseFloat(cost) || 0), 0);
        const viaticosTotal = parseFloat(viaticos) || 0;
        return service + materialsTotal + equipmentsTotal + viaticosTotal;
    };

    const confirmBudget = async () => {
        const budgetList: BudgetDto[] = [];

        // Agregar servicio
        if (serviceCost && parseFloat(serviceCost) > 0) {
            budgetList.push({
                conceptType: 'SERVICE',
                amount: parseFloat(serviceCost),
                description: 'Servicio'
            });
        }

        // Agregar materiales/refacciones
        materialsRefacciones.forEach(item => {
            if (item.name.trim() && item.cost && parseFloat(item.cost) > 0) {
                budgetList.push({
                    conceptType: 'MATERIAL',
                    amount: parseFloat(item.cost),
                    description: item.name
                });
            }
        });

        // Agregar equipos
        Object.entries(equipmentCosts).forEach(([equipment, cost]) => {
            if (cost && parseFloat(cost) > 0) {
                budgetList.push({
                    conceptType: 'EQUIPMENT',
                    amount: parseFloat(cost),
                    description: equipment
                });
            }
        });

        // Agregar viáticos
        if (viaticos && parseFloat(viaticos) > 0) {
            budgetList.push({
                conceptType: 'TRAVEL_EXPENSES',
                amount: parseFloat(viaticos),
                description: 'Viáticos'
            });
        }

        try {
            await addBudget(ticketCode, budgetList);
            // Actualizar el presupuesto localmente
            updateTicketBudgetLocally(budgetList);
            closeBudgetModal();
            // Limpiar los campos
            setServiceCost("");
            setMaterialsRefacciones([{name: "", cost: ""}]);
            setEquipmentCosts({});
            setViaticos("");
        } catch (error) {
            console.error('Error al agregar presupuesto:', error);
        }
    }

    const confirmTechnicianForTicket = () => {
        assignTicketToTechnician(selectedTechnician, ticketCode, serviceDate).then(
            () => {
                updateTicketTechnicianLocally(
                    selectedTechnician && selectedTechnicianName
                        ? `${selectedTechnicianName} - ${selectedTechnician}`
                        : "",
                    serviceDate
                );
                closeAssignModal();
                const newState = (selectedTechnician !== "" && selectedTechnicianName !== "")
                    ? "Asignado"
                    : "Aprobado";

                updateTicketStateLocally(newState, ticketCode);
            }
        );
    }

    return (
        <div className="tickets-info-view-container">
            <div className="tickets-info-view-header">
                <button onClick={onBack}>REGRESAR</button>
                <div className="tickets-info-view-header-title">
                    <p>TICKET</p>
                </div>
                {(ticket?.ticketState === "APPROVED" || ticket?.ticketState === "ASSIGNED") && (
                    <>
                        <span className="material-icons-outlined icon-unselected" onClick={handleAssignTicketIcon}>
                            person
                        </span>
                    </>
                )}
                {(ticket?.ticketState === "CREATED" || ticket?.ticketState === "APPROVED" || ticket?.ticketState === "ASSIGNED") && (
                    <>
                        <span className="material-icons-outlined icon-unselected" onClick={handleAddBudget}>
                            attach_money
                        </span>
                    </>
                )}
            </div>
            <div className="admin-tickets-info-wrapper">
                <div className="tickets-info-view-body-info">
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">CLAVE DE TICKET</div>
                        <div className="value-label">{ticketCode}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">TIPO DE SERVICIO</div>
                        <div className="value-label">{serviceTypeMap.get(ticket?.service)}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">ESTADO</div>
                        <div className="value-label">{ticketStateMap.get(ticket?.ticketState)}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">DESCRIPCION</div>
                        <div className="value-label">{ticket?.description}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">COMENTARIO ADICIONAL</div>
                        <div className="value-label">{ticket?.additionalComment}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">TÉCNICO ASIGNADO</div>
                        <div className="value-label">{ticket?.assignedTechnician}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">FECHA DE CREACIÓN</div>
                        <div className="value-label">{formattedDate}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">CLIENTE</div>
                        <div className="value-label">{additionalInfo.CLIENTE}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">PLANTA</div>
                        <div className="value-label">{additionalInfo.PLANTA}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">FECHA DE SERVICIO</div>
                        <div className="value-label">{ticket?.serviceDate}</div>
                    </div>
                </div>
                <div className="admin-tickets-additional-info">
                    <div className="ticket-additional-info-attribute">
                        <div className="attribute-label">EQUIPOS</div>
                        <div>
                            <ul className="additional-info-attribute-list">
                                {ticket?.equipments?.map((equipment: any, index: any) => (
                                    <li key={index}>
                                        {equipment}
                                    </li>))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {isModalVisible && (
                <div className="modal-overlay">
                    <div className="modal">
                        <p>Asignar técnico a ticket</p>
                        <select
                            id="atc"
                            value={selectedTechnician}
                            onChange={(e) => {
                                const selectedUsername = e.target.value;
                                const selectedName = technicians.find((technician: any) => technician.username === selectedUsername);
                                setSelectedTechnician(selectedUsername);
                                setSelectedTechnicianName(selectedName ? selectedName.fullName : "");
                            }}
                            style={{
                                border: "1px solid black",
                                backgroundColor: "white"
                            }}
                        >
                            <option value=""></option>
                            {technicians.map((option: any) => (
                                <option key={option.username} value={option.username}>
                                    {option.fullName} - {option.username}
                                </option>
                            ))}
                        </select>
                        <div style={{marginTop: "10px"}}>
                            <label htmlFor="serviceDate" style={{display: "block", marginBottom: "5px"}}>
                                Fecha de servicio
                            </label>
                            <input
                                type="date"
                                id="serviceDate"
                                value={serviceDate}
                                onChange={(e) => setServiceDate(e.target.value)}
                                style={{
                                    border: "1px solid black",
                                    backgroundColor: "white",
                                    padding: "5px",
                                    width: "100%"
                                }}
                            />
                        </div>
                        <div className="modal-buttons">
                            <button
                                onClick={confirmTechnicianForTicket}
                                disabled={assigningTechnician}
                                className={assigningTechnician ? "disabled-button" : ""}
                            >
                                Aceptar
                            </button>
                            <button
                                onClick={closeAssignModal}
                                disabled={assigningTechnician}
                                className={assigningTechnician ? "disabled-button" : ""}
                            >
                                Cancelar
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {isBudgetModalVisible && (
                <div className="modal-overlay">
                    <div className="modal budget-modal">
                        <p style={{fontWeight: "bold", fontSize: "18px"}}>Agregar Presupuesto</p>

                        <div style={{maxHeight: "60vh", overflowY: "auto", padding: "10px"}}>
                            {/* Servicio */}
                            <div style={{marginBottom: "20px"}}>
                                <label htmlFor="serviceCost" style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                    Servicio
                                </label>
                                <input
                                    type="number"
                                    id="serviceCost"
                                    value={serviceCost}
                                    onChange={(e) => setServiceCost(e.target.value)}
                                    placeholder="Costo del servicio"
                                    style={{
                                        border: "1px solid black",
                                        backgroundColor: "white",
                                        padding: "8px",
                                        width: "100%",
                                        borderRadius: "4px"
                                    }}
                                />
                            </div>

                            {/* Materiales/Refacciones */}
                            <div style={{marginBottom: "20px"}}>
                                <label style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                    Materiales/Refacciones
                                </label>
                                {materialsRefacciones.map((item, index) => (
                                    <div key={index} style={{display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center"}}>
                                        <input
                                            type="text"
                                            value={item.name}
                                            onChange={(e) => updateMaterialRefaccion(index, "name", e.target.value)}
                                            placeholder="Nombre del material/refacción"
                                            style={{
                                                border: "1px solid black",
                                                backgroundColor: "white",
                                                padding: "8px",
                                                flex: "1",
                                                borderRadius: "4px"
                                            }}
                                        />
                                        <input
                                            type="number"
                                            value={item.cost}
                                            onChange={(e) => updateMaterialRefaccion(index, "cost", e.target.value)}
                                            placeholder="Costo"
                                            style={{
                                                border: "1px solid black",
                                                backgroundColor: "white",
                                                padding: "8px",
                                                width: "120px",
                                                borderRadius: "4px"
                                            }}
                                        />
                                        {materialsRefacciones.length > 1 && (
                                            <button
                                                onClick={() => removeMaterialRefaccion(index)}
                                                style={{
                                                    backgroundColor: "#f44336",
                                                    color: "white",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    padding: "8px",
                                                    cursor: "pointer",
                                                    fontSize: "12px"
                                                }}
                                            >
                                                Eliminar
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    onClick={addMaterialRefaccion}
                                    style={{
                                        backgroundColor: "#4CAF50",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        padding: "8px 16px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        marginTop: "5px"
                                    }}
                                >
                                    + Agregar Material/Refacción
                                </button>
                            </div>

                            {/* Equipos */}
                            <div style={{marginBottom: "20px"}}>
                                <label style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                    Equipos
                                </label>
                                {ticket?.equipments?.map((equipment: string, index: number) => (
                                    <div key={index} style={{display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center"}}>
                                        <span style={{
                                            border: "1px solid black",
                                            backgroundColor: "#f0f0f0",
                                            padding: "8px",
                                            flex: "1",
                                            borderRadius: "4px"
                                        }}>
                                            {equipment}
                                        </span>
                                        <input
                                            type="number"
                                            value={equipmentCosts[equipment] || ""}
                                            onChange={(e) => updateEquipmentCost(equipment, e.target.value)}
                                            placeholder="Costo"
                                            style={{
                                                border: "1px solid black",
                                                backgroundColor: "white",
                                                padding: "8px",
                                                width: "120px",
                                                borderRadius: "4px"
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Viáticos */}
                            <div style={{marginBottom: "20px"}}>
                                <label htmlFor="viaticos" style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                    Viáticos
                                </label>
                                <input
                                    type="number"
                                    id="viaticos"
                                    value={viaticos}
                                    onChange={(e) => setViaticos(e.target.value)}
                                    placeholder="Costo de viáticos"
                                    style={{
                                        border: "1px solid black",
                                        backgroundColor: "white",
                                        padding: "8px",
                                        width: "100%",
                                        borderRadius: "4px"
                                    }}
                                />
                            </div>

                            {/* Total */}
                            <div style={{
                                marginTop: "20px",
                                padding: "15px",
                                backgroundColor: "#f0f0f0",
                                borderRadius: "4px",
                                border: "2px solid var(--primary-color)"
                            }}>
                                <label style={{display: "block", marginBottom: "5px", fontWeight: "700", fontSize: "16px"}}>
                                    Costo Total
                                </label>
                                <div style={{
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: "var(--primary-color)"
                                }}>
                                    ${calculateTotalCost().toFixed(2)}
                                </div>
                            </div>
                        </div>

                        <div className="modal-buttons">
                            <button
                                onClick={confirmBudget}
                                disabled={addingBudget}
                                className={addingBudget ? "disabled-button" : ""}
                            >
                                Aceptar
                            </button>
                            <button
                                onClick={closeBudgetModal}
                                disabled={addingBudget}
                                className={addingBudget ? "disabled-button" : ""}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SupervisorTicketInfo;
